import { minCharactersForSuggestion, typeColorMap } from "./consts";
import { createRegexRenderer } from "rich-textarea";
import { Suggestion, SuggestionType } from "./types";

export const promptValidForSuggestions = (
  input: string,
  cursorPosition: number
) => {
  const leftPart = input.slice(0, cursorPosition);
  const rightPart = input.slice(cursorPosition);
  const leftWordMatch = leftPart.match(/(\S+)?$/); // Word to the left of the cursor
  const leftWord = leftWordMatch ? leftWordMatch[0] : "";
  const charAfterCursor = rightPart.charAt(0); // First character after the cursor
  const charBeforeCursor = leftPart.charAt(leftPart.length - 1);
  const allowedAfterWordChars = [
    " ",
    ".",
    ",",
    ";",
    "!",
    "?",
    ")",
    "]",
    "}",
    "",
  ];
  return (
    /[a-zA-Z]/.test(charBeforeCursor) &&
    leftWord.length >= minCharactersForSuggestion &&
    allowedAfterWordChars.includes(charAfterCursor)
  );
};

export const getFeDisplaySuggestion = (suggestion: Suggestion) => {
  return `${suggestion.fe_display} (${suggestion.tag})`;
};

export const replaceLastWord = (
  replacement: string,
  text: string,
  cursorPosition: number
) => {
  // Split the text into left and right parts based on the cursor position
  const leftPart = text.slice(0, cursorPosition); // Text to the left of the cursor
  const rightPart = text.slice(cursorPosition); // Text to the right of the cursor

  // Match the last word in the left part
  const leftWordMatch = leftPart.match(/(\S+)?$/); // Match the last sequence of non-whitespace characters
  const lastTypedWord = leftWordMatch ? leftWordMatch[0] : "";

  // Replace the last typed word in the left part with the replacement
  const nonBreakingSegment = `\u00A0${replacement.replace(
    /\s+/g,
    "\u00A0"
  )}\u00A0`;
  const updatedLeftPart = leftPart.replace(
    new RegExp(`${lastTypedWord}$`),
    nonBreakingSegment
  );

  // Combine the updated left part with the unchanged right part
  const updatedText =
    rightPart === ""
      ? updatedLeftPart + " "
      : updatedLeftPart + " " + rightPart;

  return updatedText;
};

export const normalizeMessage = (text: string) => {
  // Replace non-breaking spaces (\u00A0) with a regular space
  let updatedText = text.replace(/\u00A0/g, " ");

  // Replace multiple spaces (including double spaces) with a single space
  updatedText = updatedText.replace(/\s+/g, " ");

  // Trim leading and trailing spaces, if any
  updatedText = updatedText.trim();

  return updatedText;
};

const normalizeSpaces = (str: string) => str.replace(/\u00A0/g, " ");

export const getBePrompt = (
  prompt: string,
  feToBeTermsDict: Record<string, string>
) => {
  const bePrompt = Object.keys(feToBeTermsDict).reduce((result, key) => {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape regex special characters
    const regexKey = escapedKey.replace(/\s+/g, "(\\s|\\u00A0)+"); // Allow for flexible spaces and non-breaking spaces
    const regex = new RegExp(regexKey, "gi"); // Create regex with case-insensitive and global flags

    return result.replace(regex, feToBeTermsDict[key]);
  }, normalizeSpaces(prompt));

  return normalizeMessage(bePrompt);
};

type StyleObject = {
  borderRadius: string;
  outline: string;
  color: string;
};
type RegexStyleTuple = [RegExp, StyleObject];

export const getRenderer = (
  inputKeywords: string[],
  feToTypeDict: Record<string, SuggestionType | undefined>
): ReturnType<typeof createRegexRenderer> | undefined => {
  const regexArray = inputKeywords
    .map((term) => {
      try {
        const escapeRegex = (str: string) =>
          str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const escapedTerm = escapeRegex(term);
        const termType: SuggestionType = feToTypeDict[term] ?? "defaultType";
        const color = typeColorMap[termType] || "default";
        // Replace spaces in the term with non-breaking spaces for the regex
        const regexString = `\\u00A0${escapedTerm.replace(
          /\s+/g,
          "\\u00A0"
        )}\\u00A0`;

        // Create the regex with non-breaking spaces
        const regex = new RegExp(regexString, "gi");
        return [
          regex,
          {
            borderRadius: "4px",
            outline: `solid 1px ${color}`,
            color: color,
          },
        ];
      } catch (error) {
        console.error(`Error creating regex for term: ${term}`, error);
        return null; // Return null in case of error
      }
    })
    .filter((item): item is RegexStyleTuple => item !== null);
  return regexArray.length > 0 ? createRegexRenderer(regexArray) : undefined;
};
